import React, { useEffect, useState, useRef } from "react";
import toast from "react-hot-toast";
import { FaTrash, FaFile } from "react-icons/fa6";
import { useSelector } from "react-redux";

import api from "../../../../../services/api";
import { readFileAsync } from "../../../../../utils";

// Define the default Selego prompts
const defaultPrompts = {
  en: `Based on the project description, please generate an initial set of epic proposals with time estimates in days following a Features-First approach based on an MVP.

As a technical partner specializing in Tech, Product, Data/IA & Growth, be assertive in your recommendations and break down the project into:

1. Core functionality epics (essential features that MUST be in the MVP)
2. User experience epics (necessary for the MVP)
3. Growth & analytics features (required for initial success)

Be decisive in categorizing features - clearly separate what's essential now versus what should come later that you don't include in your response. Focus on delivering maximum value with minimum complexity for the MVP.`,

  fr: `Sur base de la description du projet, génère une série initiale de propositions d'epics avec des estimations de temps en jours suivant une approche Features-First basée sur un MVP.

En tant que partenaire technique spécialisé en Tech, Produit, Données/IA & Croissance, soit force de proposition et décomposez le projet en:

1. Epics de fonctionnalités essentielles (éléments INDISPENSABLES pour le MVP)
2. Epics d'expérience utilisateur (nécessaires pour le MVP)
3. Epics de croissance et d'analytique (requis pour le succès initial)

Soit décisif dans la catégorisation des fonctionnalités - sépare clairement ce qui est essentiel maintenant de ce qui devrait venir plus tard que tu n'inclus pas dans ta réponse. Concentre-toi sur la livraison d'une valeur maximale avec une complexité minimale pour le MVP.`,
};

export default ({ project }) => {
  const [epics, setEpics] = useState([]);
  const [generatedEpics, setGeneratedEpics] = useState([]);
  const [isStartingConversation, setIsStartingConversation] = useState(false);
  const [isCreatingEpic, setIsCreatingEpic] = useState(false);
  const [isDeletingEpic, setIsDeletingEpic] = useState(false);
  const [contextualEpics, setContextualEpics] = useState([]);
  const [activeTab, setActiveTab] = useState("conversation");
  const [aiConversation, setAiConversation] = useState([]);
  const [groupedEpics, setGroupedEpics] = useState({});
  const [expandedModules, setExpandedModules] = useState(new Set());

  useEffect(() => {
    getEpics();
  }, []);

  async function getEpics() {
    const { data } = await api.post("/epic/search", { project_id: project._id });
    setEpics(data);

    // Regrouper les epics par module
    const grouped = data.reduce((acc, epic) => {
      const module = epic.module || "Sans module";
      if (!acc[module]) acc[module] = [];
      acc[module].push(epic);
      return acc;
    }, {});
    setGroupedEpics(grouped);

    // Ouvrir tous les modules par défaut
    setExpandedModules(new Set(Object.keys(grouped)));
  }

  // Toggle pour ouvrir/fermer un module
  const toggleModule = (module) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(module)) {
      newExpanded.delete(module);
    } else {
      newExpanded.add(module);
    }
    setExpandedModules(newExpanded);
  };

  const addEpicToRoadmap = async (epic) => {
    try {
      setIsCreatingEpic(true);
      const { data } = await api.post("/epic", {
        project_id: project._id,
        title: epic.title,
        days_required: epic.days_required || epic.days || 0,
        comments: epic.comments || [{ text: "" }],
        module: epic.module || "",
        estimation: epic.days_required * 500 || 0,
      });

      setEpics([...epics, data]);

      if (generatedEpics.some((e) => e.title === epic.title)) {
        setGeneratedEpics(generatedEpics.filter((e) => e.title !== epic.title));
      }

      toast.success("Epic added to roadmap!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to add epic to roadmap");
    } finally {
      setIsCreatingEpic(false);
    }
  };

  const addEpicToContext = (epic) => {
    if (!contextualEpics.some((e) => e.title === epic.title)) {
      setContextualEpics([...contextualEpics, epic]);
      setActiveTab("conversation");
    }
  };

  const removeEpicFromContext = (epicIndex) => {
    setContextualEpics(contextualEpics.filter((_, index) => index !== epicIndex));
  };

  const deleteEpicFromRoadmap = async (epicId) => {
    if (!confirm("Are you sure you want to delete this epic from the roadmap?")) return;

    try {
      setIsDeletingEpic(true);
      await api.remove(`/epic/${epicId}`);

      // Update local state
      setEpics(epics.filter((epic) => epic._id !== epicId));

      // Also remove from contextual epics if present
      setContextualEpics(contextualEpics.filter((epic) => epic._id !== epicId));

      toast.success("Epic deleted from roadmap!");
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete epic from roadmap");
    } finally {
      setIsDeletingEpic(false);
    }
  };

  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col md:flex-row gap-6">
          <ProjectInfoPanel
            project={project}
            setGeneratedEpics={setGeneratedEpics}
            setIsStartingConversation={setIsStartingConversation}
            isStartingConversation={isStartingConversation}
            generatedEpics={generatedEpics}
            isCreatingEpic={isCreatingEpic}
            addEpicToRoadmap={addEpicToRoadmap}
            contextualEpics={contextualEpics}
            removeEpicFromContext={removeEpicFromContext}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            aiConversation={aiConversation}
            setAiConversation={setAiConversation}
          />

          <div className="w-full md:w-4/12">
            <ExistingEpicsPanel epics={epics} addEpicToContext={addEpicToContext} deleteEpicFromRoadmap={deleteEpicFromRoadmap} isDeletingEpic={isDeletingEpic} />
          </div>
        </div>
      </div>
    </div>
  );
};

const ProjectInfoPanel = ({
  project,
  setGeneratedEpics,
  setIsStartingConversation,
  isStartingConversation,
  generatedEpics,
  isCreatingEpic,
  addEpicToRoadmap,
  contextualEpics,
  removeEpicFromContext,
  activeTab,
  setActiveTab,
  aiConversation,
  setAiConversation,
}) => {
  const { user } = useSelector((state) => state.Auth);
  const [brief, setBrief] = useState(project.builder_brief);
  const [isSaving, setIsSaving] = useState(false);
  const [promptLanguage, setPromptLanguage] = useState("en"); // Default language setting
  const [showPromptOptions, setShowPromptOptions] = useState(false);

  // Initialize aiResponse (no longer initialized with default prompt)
  const [aiResponse, setAiResponse] = useState("");
  const [builderUrls, setBuilderUrls] = useState(project.builder_urls || []);
  const [uploading, setUploading] = useState(false);
  const conversationEndRef = useRef(null);

  // Référence pour le menu déroulant
  const promptMenuRef = useRef(null);

  // Gestionnaire pour fermer le menu quand on clique en dehors
  useEffect(() => {
    function handleClickOutside(event) {
      if (promptMenuRef.current && !promptMenuRef.current.contains(event.target)) {
        setShowPromptOptions(false);
      }
    }

    // Ajouter l'écouteur d'événement lors de l'affichage du menu
    if (showPromptOptions) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPromptOptions]);

  const scrollToBottom = () => {
    if (conversationEndRef.current) {
      conversationEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [aiConversation]);

  const sendAiMessage = async () => {
    if (!aiResponse.trim()) return;

    try {
      setIsStartingConversation(true);

      const updatedConversation = [...aiConversation, { role: "user", message: aiResponse }];
      setAiConversation(updatedConversation);

      // Vider la textarea immédiatement après l'envoi du message
      setAiResponse("");

      const { data } = await api.post("/project/builder", {
        project_id: project._id,
        conversation: updatedConversation,
        builder_brief: brief,
        context_epics: contextualEpics,
        epics: generatedEpics,
        latest_message: aiResponse,
      });

      const finalConversation = [...updatedConversation, { role: "assistant", message: data.message, epics: data.epics || [] }];
      setAiConversation(finalConversation);

      if (data.message) setActiveTab("conversation");

      if (data.epics && Array.isArray(data.epics)) {
        setGeneratedEpics(data.epics);
      }

      toast.success("AI response received!");
    } catch (error) {
      console.log(error);
      toast.error("Error connecting to AI");
    } finally {
      setIsStartingConversation(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const uploadedFiles = Array.from(e.target.files);
    if (!uploadedFiles.length) return;

    // Check if files are PDFs
    for (const file of uploadedFiles) {
      if (file.type !== "application/pdf") {
        toast.error("Only PDF files are allowed");
        return;
      }
    }

    setUploading(true);
    try {
      // Prepare files for the multiple upload endpoint
      const filesToUpload = [];

      for (const file of uploadedFiles) {
        // Convert file to base64 for the API
        const filePromise = await readFileAsync(file);
        filesToUpload.push({ rawBody: filePromise, name: file.name });
      }

      // Use the file/multiple endpoint
      const { data: urls } = await api.post("/file/multiple", {
        files: filesToUpload,
        folder: `/project/${project._id}/builder`,
      });

      // Create new document objects
      const newDocuments = urls.map((url, index) => {
        const file = index < uploadedFiles.length ? uploadedFiles[index] : null;
        const fileName = file?.name || `file-${index}`;
        return {
          name: fileName,
          url,
          userId: user._id,
          userName: user.name,
          userAvatar: user.avatar,
          created_at: new Date().toISOString(),
        };
      });

      // Update builderUrls state
      const updatedUrls = [...builderUrls, ...newDocuments];
      setBuilderUrls(updatedUrls);

      // Save the updated URLs to the project
      await api.put(`/project/${project._id}`, {
        builder_brief: brief,
        builder_urls: updatedUrls,
      });

      toast.success("PDF documents uploaded successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error uploading PDF documents");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docIndex) => {
    if (!confirm("Are you sure you want to delete this document?")) return;

    try {
      const updatedUrls = builderUrls.filter((_, index) => index !== docIndex);
      setBuilderUrls(updatedUrls);

      // Save the updated URLs to the project
      await api.put(`/project/${project._id}`, {
        builder_brief: brief,
        builder_urls: updatedUrls,
      });

      toast.success("Document deleted successfully");
    } catch (error) {
      console.error(error);
      toast.error("Error deleting document");
    }
  };

  const saveProjectInfo = async () => {
    try {
      setIsSaving(true);
      await api.put(`/project/${project._id}`, {
        builder_brief: brief,
        builder_urls: builderUrls,
      });
      toast.success("Project information saved successfully!");
    } catch (error) {
      console.log(error);
      toast.error("Error saving project information");
    } finally {
      setIsSaving(false);
    }
  };

  const insertDefaultPrompt = (language) => {
    setAiResponse(defaultPrompts[language]);
    setShowPromptOptions(false);
  };

  return (
    <div className="w-full md:w-8/12">
      <div className="bg-white border border-gray-200 rounded shadow-sm mb-4">
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              data-tab="conversation"
              className={`px-4 py-3 text-sm font-medium ${activeTab === "conversation" ? "text-blue-600 border-b-2 border-blue-500" : "text-gray-600 hover:text-gray-800"}`}
              onClick={() => setActiveTab("conversation")}>
              Conversation
            </button>
            <button
              className={`px-4 py-3 text-sm font-medium ${activeTab === "info" ? "text-blue-600 border-b-2 border-blue-500" : "text-gray-600 hover:text-gray-800"}`}
              onClick={() => setActiveTab("info")}>
              Project Details
            </button>
          </div>
        </div>
        <div>
          {activeTab === "info" && (
            <div className="mb-4 p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Project Description</label>
              <textarea
                className="w-full h-[200px] p-3 border border-gray-300 rounded text-gray-700 text-sm resize-none focus:border-gray-400 focus:outline-none"
                value={brief}
                onChange={(e) => setBrief(e.target.value)}
                placeholder="Describe your project here..."
              />

              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Project Documents (PDF only)</label>
                <div className="flex items-center justify-center w-full mb-4">
                  <label
                    className={`flex flex-col items-center justify-center w-full h-24 border-2 border-dashed rounded-lg cursor-pointer ${
                      uploading ? "bg-gray-100 border-gray-400" : "border-gray-300 bg-gray-50 hover:bg-gray-100"
                    }`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (uploading) return;

                      const droppedFiles = e.dataTransfer.files;
                      if (droppedFiles.length > 0) handlePdfUpload({ target: { files: droppedFiles } });
                    }}>
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      {uploading ? (
                        <>
                          <svg className="w-6 h-6 mb-2 text-gray-500 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <p className="text-xs text-gray-500">Uploading...</p>
                        </>
                      ) : (
                        <>
                          <FaFile className="w-6 h-6 mb-2 text-gray-500" />
                          <p className="text-xs font-medium text-gray-700">
                            <span className="font-semibold">Click to upload PDF</span> or drag and drop
                          </p>
                        </>
                      )}
                    </div>
                    <input id="pdf-file" type="file" className="hidden" multiple accept="application/pdf" onChange={handlePdfUpload} disabled={uploading} />
                  </label>
                </div>

                {builderUrls && builderUrls.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {builderUrls.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-md hover:bg-gray-100 transition-colors">
                        <div className="flex items-center space-x-2 overflow-hidden">
                          <FaFile className="w-4 h-4 text-red-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-700 truncate">{doc.name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer nofollow"
                            className="text-xs text-green-600 hover:text-green-800 px-2 py-1 rounded hover:bg-green-50">
                            View
                          </a>
                          <button type="button" onClick={() => handleDeleteDocument(index)} className="text-xs text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50">
                            <FaTrash className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "conversation" && (
            <div className="mb-4">
              <div className="max-h-[550px] relative overflow-y-auto mb-3 p-2 px-4">
                <div className="space-y-1">
                  {aiConversation.map((message, index) => (
                    <div key={index}>
                      <div className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                        <div
                          className={`max-w-[85%] p-3.5 rounded-xl shadow-sm ${
                            message.role === "user" ? "bg-blue-600 text-white rounded-tr-none" : "bg-gray-100 text-gray-800 rounded-tl-none border border-gray-200"
                          }`}>
                          <div className="text-sm whitespace-pre-line">{message.message}</div>
                        </div>
                      </div>

                      {/* Affichage des epics après un message de l'assistant si c'est le dernier message */}
                      {message.role === "assistant" && index === aiConversation.length - 1 && generatedEpics.length > 0 && (
                        <div className="ml-8 mt-2 grid gap-2">
                          {generatedEpics.map((epic, epicIndex) => {
                            const description = (epic.comments || []).length > 0 ? epic.comments[0].text : "";
                            return (
                              <div key={epicIndex} className="border border-gray-200 bg-white rounded-lg p-3 shadow-sm hover:bg-gray-50 transition-all">
                                <div className="flex justify-between items-start gap-2">
                                  <div className="flex-1">
                                    <div className="flex justify-between items-center mb-2">
                                      <h4 className="text-sm font-medium text-gray-800 truncate" title={epic.title}>
                                        {epic.title}
                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                          {epic.days_required} days
                                        </span>
                                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                          {epic.module}
                                        </span>
                                      </h4>
                                      <button
                                        onClick={() => addEpicToRoadmap(epic)}
                                        disabled={isCreatingEpic}
                                        className={`text-xs text-white px-3 py-1 rounded-full bg-green-600 hover:bg-green-700 transition-colors ${
                                          isCreatingEpic ? "opacity-60 cursor-not-allowed" : ""
                                        }`}>
                                        {isCreatingEpic ? "..." : "Add"}
                                      </button>
                                    </div>
                                    <p className="text-xs text-gray-600" title={description}>
                                      {description}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}

                  {isStartingConversation && (
                    <div className="flex justify-start">
                      <div className="bg-gray-100 border border-gray-200 p-4 rounded-xl rounded-tl-none shadow-sm">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
                          <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.4s" }}></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={conversationEndRef} />
                </div>
              </div>

              {contextualEpics.length > 0 && (
                <div className="w-full bg-blue-50 py-2 px-3 border-b border-blue-100">
                  <div className="flex items-center gap-2">
                    <h4 className="text-xs font-medium text-blue-700">Context:</h4>
                    <div className="flex flex-wrap gap-1">
                      {contextualEpics.map((epic, index) => (
                        <div key={index} className="inline-flex items-center bg-white border border-blue-200 rounded-full px-2 py-0.5 text-xs">
                          <span className="font-medium truncate max-w-[160px]" title={epic.title}>
                            {epic.title}
                          </span>
                          <button onClick={() => removeEpicFromContext(index)} className="ml-1 text-gray-400 hover:text-red-500" title="Remove from context">
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-2 mt-4 relative  px-4">
                <textarea
                  className="flex-1 p-3.5 border border-gray-300 rounded-xl text-gray-700 text-sm resize-vertical focus:border-blue-400 focus:outline-none shadow-sm min-h-[96px]"
                  value={aiResponse}
                  onChange={(e) => setAiResponse(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      sendAiMessage();
                    }
                  }}
                  placeholder={
                    contextualEpics.length > 0 ? "Give your feedback on the selected epics or ask for modifications..." : "Ask a question or provide more information..."
                  }
                  rows={3}
                />
                <div className="flex flex-col justify-end gap-2">
                  <div className="relative" ref={promptMenuRef}>
                    <button
                      type="button"
                      onClick={() => setShowPromptOptions(!showPromptOptions)}
                      className="bg-gray-100 text-gray-700 h-10 px-3 rounded-xl hover:bg-gray-200 transition-colors flex items-center justify-center gap-1.5"
                      title="Insert template prompt">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round">
                        <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                      </svg>
                      <span className="text-xs font-medium">Template</span>
                    </button>

                    {showPromptOptions && (
                      <div className="absolute bottom-12 right-0 bg-white shadow-lg rounded-lg border border-gray-200 py-2 w-36 z-10">
                        <button onClick={() => insertDefaultPrompt("en")} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          English
                        </button>
                        <button onClick={() => insertDefaultPrompt("fr")} className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          Français
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    onClick={sendAiMessage}
                    disabled={isStartingConversation || !aiResponse.trim()}
                    className={`bg-blue-600 text-white px-5 h-10 font-medium rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center ${
                      isStartingConversation || !aiResponse.trim() ? "opacity-60 cursor-not-allowed" : ""
                    }`}>
                    {isStartingConversation ? (
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab !== "conversation" && (
            <div className="flex flex-col gap-3">
              <button
                onClick={saveProjectInfo}
                disabled={isSaving}
                className={`bg-gray-100 text-gray-800 px-4 py-2 text-sm font-medium rounded border border-gray-300 hover:bg-gray-200 ${
                  isSaving ? "opacity-60 cursor-not-allowed" : ""
                }`}>
                {isSaving ? "Saving..." : "Save Information"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ExistingEpicsPanel = ({ epics, addEpicToContext, deleteEpicFromRoadmap, isDeletingEpic }) => {
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [groupedEpics, setGroupedEpics] = useState({});
  const [expandedModules, setExpandedModules] = useState(new Set());

  // Regrouper les epics par module lors du changement des epics
  useEffect(() => {
    const grouped = epics.reduce((acc, epic) => {
      const module = epic.module || "Sans module";
      if (!acc[module]) acc[module] = [];
      acc[module].push(epic);
      return acc;
    }, {});
    setGroupedEpics(grouped);
    // Ouvrir tous les modules par défaut
    setExpandedModules(new Set(Object.keys(grouped)));
  }, [epics]);

  const toggleDescription = (epicId) => {
    setExpandedDescriptions({
      ...expandedDescriptions,
      [epicId]: !expandedDescriptions[epicId],
    });
  };

  const toggleModule = (module) => {
    const newExpanded = new Set(expandedModules);
    if (newExpanded.has(module)) {
      newExpanded.delete(module);
    } else {
      newExpanded.add(module);
    }
    setExpandedModules(newExpanded);
  };

  return (
    <div className="h-[700px] overflow-y-auto">
      <div className="space-y-2">
        {Object.keys(groupedEpics).length > 0 ? (
          Object.entries(groupedEpics).map(([module, moduleEpics]) => (
            <div key={module} className="mb-2">
              <div
                onClick={() => toggleModule(module)}
                className="flex items-center justify-between p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100 border border-gray-200">
                <div className="flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 text-gray-500 transform transition-transform duration-200 ${expandedModules.has(module) ? "rotate-90" : ""}`}
                    viewBox="0 0 20 20"
                    fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="font-medium text-gray-700">{module}</span>
                  <span className="text-xs text-gray-500">({moduleEpics.length})</span>
                </div>
              </div>

              {expandedModules.has(module) && (
                <div className="pl-2 mt-1 space-y-2">
                  {moduleEpics.map((epic, index) => {
                    const description = epic.comments && epic.comments.length > 0 ? epic.comments[0].text : "";
                    const isExpanded = expandedDescriptions[epic._id] || false;

                    return (
                      <div key={index} className="border border-gray-200 rounded p-2 bg-white hover:bg-gray-50 relative">
                        <button
                          onClick={() => deleteEpicFromRoadmap(epic._id)}
                          disabled={isDeletingEpic}
                          className={`absolute top-1.5 right-1 text-red-600 hover:text-red-800 p-1 rounded-full hover:bg-red-50 ${
                            isDeletingEpic ? "opacity-60 cursor-not-allowed" : ""
                          }`}
                          title="Delete epic from roadmap">
                          <FaTrash className="w-3 h-3" />
                        </button>

                        <div className="flex flex-col">
                          <div>
                            <h4 className="text-sm font-medium text-gray-800 truncate pr-3" title={epic.title}>
                              {epic.title}
                            </h4>

                            {description && (
                              <div className="mt-1">
                                <p className={`text-xs text-gray-600 ${!isExpanded ? "line-clamp-2" : ""}`}>{description}</p>
                              </div>
                            )}

                            <div className="flex items-center justify-between mt-1.5">
                              <div className="flex flex-wrap gap-1">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{epic.days_required}j</span>
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{epic.status}</span>
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {description.length > 60 && (
                                  <button onClick={() => toggleDescription(epic._id)} className="text-xs text-blue-600 hover:text-blue-800 mt-0.5">
                                    {isExpanded ? "Show less" : "Show more"}
                                  </button>
                                )}
                                <button onClick={() => addEpicToContext(epic)} className="flex-shrink-0 bg-slate-600 text-white px-2 py-0.5 text-xs rounded hover:bg-slate-700">
                                  Add in context
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))
        ) : (
          <div className="text-center py-10">
            <p className="text-gray-500">No epics in the roadmap yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};
